import { useEffect, useRef } from 'react'

/**
 * Centrale keyboard-shortcut registry (Sprint A).
 *
 *   useGlobalShortcuts([
 *     { key: 'j', meta: true, id: 'nav-panel-toggle', preventDefault: true, handler: () => togglePanel() },
 *     { key: 'k', meta: true, id: 'global-search',    handler: () => openSearch() },
 *   ])
 *
 * Regels:
 * - `meta: true`  → ⌘ op Mac, Ctrl op Windows/Linux (metaKey of ctrlKey).
 * - `id`          → voorkomt dubbele registratie (registry is op id gekeyd).
 * - `shift`/`alt` → optionele extra modifiers.
 * - cleanup gebeurt via de useEffect-return.
 * - GEEN preventDefault, tenzij expliciet `preventDefault: true`.
 *
 * Eén window-keydown-listener voor de hele app; meerdere componenten kunnen hun
 * shortcuts registreren zonder dubbele listeners.
 */
const registry = new Map() // id -> { key, meta, shift, alt, preventDefault, run }
let listenerAttached = false

function handleKeyDown(e) {
  for (const sc of registry.values()) {
    if (!e.key || e.key.toLowerCase() !== sc.key.toLowerCase()) continue
    const wantsMeta = !!sc.meta
    const hasMeta = e.metaKey || e.ctrlKey
    if (wantsMeta !== hasMeta) continue           // meta vereist? dan moet ⌘/Ctrl ingedrukt zijn (en omgekeerd)
    if (sc.shift && !e.shiftKey) continue
    if (sc.alt && !e.altKey) continue
    if (sc.preventDefault) e.preventDefault()
    sc.run(e)
  }
}

function ensureListener() {
  if (!listenerAttached && typeof window !== 'undefined') {
    window.addEventListener('keydown', handleKeyDown)
    listenerAttached = true
  }
}

export function useGlobalShortcuts(shortcuts) {
  // Bewaar de laatste handlers in een ref, zodat de registry-wrapper altijd de
  // actuele closure aanroept zonder bij elke render te her-registreren.
  const ref = useRef(shortcuts)
  ref.current = shortcuts

  // Signature: alleen her-registreren als de set shortcuts (id/toets/modifiers) wijzigt.
  const signature = (shortcuts || [])
    .map((s) => `${s?.id}:${s?.key}:${s?.meta ? 1 : 0}${s?.shift ? 1 : 0}${s?.alt ? 1 : 0}:${s?.preventDefault ? 1 : 0}`)
    .join('|')

  useEffect(() => {
    ensureListener()
    const ids = []
    for (const sc of (shortcuts || [])) {
      if (!sc || !sc.id || !sc.key) continue
      ids.push(sc.id)
      registry.set(sc.id, {
        key: sc.key,
        meta: sc.meta,
        shift: sc.shift,
        alt: sc.alt,
        preventDefault: sc.preventDefault,
        run: (e) => {
          const latest = (ref.current || []).find((x) => x && x.id === sc.id)
          ;(latest?.handler || sc.handler)?.(e)
        },
      })
    }
    return () => { ids.forEach((id) => registry.delete(id)) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature])
}

export default useGlobalShortcuts
